import { components } from '@api/play-account/generated';
import { Link } from '@tanstack/react-router';

import { cx } from '@libs/classnames';
import { CapsuleButton } from '@ui/components/CapsuleButton';
import { DateView, TimeView } from '@ui/components/DateView';

import { ClubLogo } from '@features/club/ui/ClubLogo';
import { ClubDto } from '@features/shared/api/typings';
import { EventClockControl } from '@features/shared/ui/EventClockControl/EventClockControl';
import { EventEntrance } from '@features/shared/ui/EventEntrance';

import styles from './PlayerTournaments.module.css';

type Props = {
  className?: string;
  tournaments: components['schemas']['TournamentShortDto'][];
  hasMore: boolean;
  loadMore?: () => void;
  clubs: ClubDto[];
  isUpcoming: boolean;
};
export function PlayerTournaments({
  className,
  tournaments,
  clubs,
  hasMore,
  loadMore,
  isUpcoming,
}: Props) {
  return (
    <div className={cx(styles.playerTournaments, className)}>
      {tournaments.map((tournament) => (
        <PlayerTournament
          key={tournament.tournamentId}
          tournament={tournament}
          club={clubs.find((c) => c.clubId === tournament.clubId)}
        />
      ))}

      {hasMore && loadMore && (
        <CapsuleButton preset="hollow-golden" onClick={loadMore}>
          Показать еще
        </CapsuleButton>
      )}

      {tournaments && tournaments.length === 0 && (
        <div className={styles.placeholder}>
          {isUpcoming ? 'На этой неделе турниров нет' : 'Нет прошедших турниров'}
        </div>
      )}
    </div>
  );
}

type PlayerTournamentProps = {
  tournament: components['schemas']['TournamentShortDto'];
  club?: ClubDto;
};
function PlayerTournament({ tournament, club }: PlayerTournamentProps) {
  if (!club) return null;

  return (
    <Link
      to="/club/$clubId/tournament/$tournamentId"
      params={{
        clubId: tournament.clubId,
        tournamentId: tournament.tournamentId,
      }}
      className={styles.tournament}
      from="/"
    >
      <div className={styles.top}>
        <div className={styles.clubHeader}>
          <div className={styles.clubTitle}>{club.title}</div>
          <ClubLogo className={styles.clubLogo} logoUrl={club.logoUrl} />
        </div>
        <div className={styles.tournamentTitle}>{tournament.title}</div>
        <EventEntrance className={styles.entrance} tournament={tournament} />
      </div>

      <div className={styles.bottom}>
        <div className={styles.row}>
          <div className={styles.clockControl}>
            <EventClockControl tournament={tournament} />
          </div>
          <div className={styles.participantsCount}>
            {tournament.status === 'Finished' ? (
              <RankDiff tournament={tournament} />
            ) : (
              <>
                {tournament.mode === 'Team' ||
                tournament.mode === 'PrivateTeam' ||
                tournament.mode === 'TwoOnTwo'
                  ? `${tournament.teamsCount}\u00A0ком`
                  : `${tournament.participantsCount}\u00A0чел`}
              </>
            )}
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.date}>
            <DateView date={tournament.startTimestampMs} />
          </div>
          <div className={styles.time}>
            {tournament.status === 'Finished' ? (
              <ParticipationResult tournament={tournament} />
            ) : (
              <TimeView date={tournament.startTimestampMs} />
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

type ParticipationResultProps = {
  tournament: components['schemas']['TournamentShortDto'];
};
function ParticipationResult({ tournament }: ParticipationResultProps) {
  let result = '';
  let isWin = false;
  if (tournament.participationResultType === 'Win') {
    isWin = true;
    result = 'Победа';
  }
  if (tournament.participationResultType === 'Loss') {
    result = 'Проигрыш';
  }
  if (tournament.participationResultType === 'Draw') {
    result = 'Ничья';
  }
  if (tournament.participationResultType === 'KnockedOut') {
    result = 'Вылет';
  }
  if (tournament.participationResultType === 'Place') {
    result = `${tournament.participationResultPlace} место`;
    if (
      tournament.participationResultPlace &&
      Number(tournament.participationResultPlace) &&
      Number(tournament.participationResultPlace) <= 3
    ) {
      isWin = true;
    }
  }

  return <div className={cx(styles.result, { [styles.win]: isWin })}>{result}</div>;
}

type RankDiffProps = {
  tournament: components['schemas']['TournamentShortDto'];
};
function RankDiff({ tournament }: RankDiffProps) {
  const delta = tournament.participationResultRatingChange || 0;
  const isWin = delta > 0;

  return (
    <div className={styles.rankDiff}>
      {tournament.participationResultRating}{' '}
      <span className={cx(styles.delta, { [styles.win]: isWin })}>
        {delta >= 0 ? `+${delta}` : delta}
      </span>
    </div>
  );
}
